// =================================================================
// Persistence: IndexedDB schema (TODO Task 1.16, 2.9)
// Stores: sessions, audit, levels (reserved Phase 2), applock-kv.
// Graceful degradation if IndexedDB unavailable (private browsing).
// =================================================================
import { openDB as idbOpen, type DBSchema, type IDBPDatabase } from 'idb';
import type { AuditEntry, MeasuredLevel, SessionRecord } from '@/types';
import type { IDBRecordAPI } from '@ascend/applock';

const DB_NAME = 'amino-pk';
const DB_VERSION = 1;

interface AmgDB extends DBSchema {
  sessions: { key: string; value: SessionRecord };
  audit: { key: string; value: AuditEntry };
  // Reserved for Phase 2 serial levels (kept as own store for queryability).
  levels: { key: string; value: MeasuredLevel & { sessionId: string } };
  // AppLock flat key-value store (encrypted mode).
  'applock-kv': { key: string; value: string };
}

export type AppDB = IDBPDatabase<AmgDB>;

let dbInstance: AppDB | null = null;
let dbUnavailable = false;

// Adapter delegation: when set, all repo operations route through AppLock's
// encrypted record-level adapter. When null, raw IndexedDB is used.
let idbAdapter: IDBRecordAPI | null = null;

export function setIDBAdapter(adapter: IDBRecordAPI | null): void {
  idbAdapter = adapter;
}

export function resetIDBAdapter(): void {
  idbAdapter = null;
}

/** Ensure the DB schema exists at the app's version before AppLock.init(). */
export async function ensureDBSchema(): Promise<void> {
  await openDB();
}

type StoreName = 'sessions' | 'audit' | 'levels' | 'applock-kv';

export async function dbPut<T>(storeName: StoreName, value: T): Promise<void> {
  if (idbAdapter) {
    return idbAdapter.put(storeName, value);
  }
  const conn = await openDB();
  if (!conn) return;
  await conn.put(storeName, value as never);
}

export async function dbGet<T>(
  storeName: StoreName,
  key: string,
): Promise<T | undefined> {
  if (idbAdapter) {
    return idbAdapter.get<T>(storeName, key);
  }
  const conn = await openDB();
  if (!conn) return undefined;
  const result = await conn.get(storeName, key);
  return result as T | undefined;
}

export async function dbGetAll<T>(storeName: StoreName): Promise<T[]> {
  if (idbAdapter) {
    return idbAdapter.getAll<T>(storeName);
  }
  const conn = await openDB();
  if (!conn) return [];
  const result = await conn.getAll(storeName);
  return result as T[];
}

export async function dbDelete(storeName: StoreName, key: string): Promise<void> {
  if (idbAdapter) {
    return idbAdapter.delete(storeName, key);
  }
  const conn = await openDB();
  if (!conn) return;
  await conn.delete(storeName, key);
}

/** Open the IndexedDB; returns null gracefully if unavailable. */
export async function openDB(): Promise<AppDB | null> {
  if (dbInstance) return dbInstance;
  if (dbUnavailable) return null;
  if (typeof indexedDB === 'undefined') {
    dbUnavailable = true;
    return null;
  }
  try {
    dbInstance = await idbOpen<AmgDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('audit')) {
          db.createObjectStore('audit', { keyPath: 'id' });
        }
        // Reserved Phase 2 serial-levels store.
        if (!db.objectStoreNames.contains('levels')) {
          db.createObjectStore('levels', { keyPath: 'id' });
        }
        // AppLock flat KV store (must exist before AppLock.init()).
        if (!db.objectStoreNames.contains('applock-kv')) {
          db.createObjectStore('applock-kv');
        }
      },
    });
    return dbInstance;
  } catch {
    dbUnavailable = true;
    return null;
  }
}

/** Whether IndexedDB persistence is available. */
export function isDBAvailable(): boolean {
  return !dbUnavailable && typeof indexedDB !== 'undefined';
}
